import React, { useMemo, useRef, useState, useCallback } from "react";
import Icon1 from "@assets/icons/Icon";
import { getCachedWorkers } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { useDispoAtelierStore, ZONE_COLORS } from "@store/dispoAtelierStore";
import type { AvailabilityBlock } from "@store/disponibiliteStore";
import { snapToGrid } from "@utils/timeGrid";
import { SNAP_HOUR } from "../../atelier/gridTypes";
import { BlockModal } from "./BlockModal";
import { useFullClosures, isDateClosed } from "@utils/closedDates";
import { ClosedDayOverlay } from "@components/ui/ClosedDayOverlay";
import { blockSegmentOnDate } from "@utils/blockSegments";

const HOUR_BLOCK_HEIGHT = 80;

function getBlockColors(block: AvailabilityBlock) {
  const zone = block.zoneType || "mecanique";
  return ZONE_COLORS[zone] || ZONE_COLORS.mecanique;
}

function formatHour(h: number): string {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}h${mins > 0 ? String(mins).padStart(2, "0") : ""}`;
}

interface BlockDragState {
  blockId: string;
  mode: "move" | "resize";
  startY: number;
  startX: number;
  origStartHour: number;
  origDuration: number;
  origColIndex: number;
  previewColIndex: number;
}

interface CreateDragState {
  colIndex: number;
  startQuarter: number;
  currentQuarter: number;
}

interface DragPreview {
  blockId?: string;
  colIndex: number;
  startHour: number;
  durationHours: number;
}

export function DayTimeGrid({
  onEditBlock,
}: {
  onEditBlock: (block: AvailabilityBlock) => void;
}) {
  const orgData = useDataStore((s) => s.orgData);
  // Anchor the visual grid on whole hours so a fractional opening hour (e.g. 7h45)
  // doesn't shift the hour labels/lines to ".75" positions.
  const openingHour = Math.floor(orgData?.config?.booking_opening_hour ?? 8);
  const HOURS_END = Math.ceil(Math.max((orgData?.config?.booking_closing_hour ?? 18) + 2, 20));
  const QUARTER_HEIGHT = HOUR_BLOCK_HEIGHT / 4; // visual 15-min grid lines
  // Snap step matches the planning atelier granularity.
  const SNAP_STEP = SNAP_HOUR;
  const STEP_HEIGHT = HOUR_BLOCK_HEIGHT * SNAP_STEP;

  const selectedDate = useDispoAtelierStore((s) => s.selectedDate);
  const selectedWorkerIds = useDispoAtelierStore((s) => s.selectedWorkerIds);
  const blocks = useDispoAtelierStore((s) => s.blocks);
  const closedDates = useFullClosures();
  const searchQuery = useDispoAtelierStore((s) => s.searchQuery);
  const activeZoneFilters = useDispoAtelierStore((s) => s.activeZoneFilters);
  const updateBlock = useDispoAtelierStore((s) => s.updateBlock);

  const [createModal, setCreateModal] = useState<{ workerId: string; date: string; startHour: number; duration: number } | null>(null);
  const blockDragRef = useRef<BlockDragState | null>(null);
  const createDragRef = useRef<CreateDragState | null>(null);
  const didDrag = useRef(false);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const colRefs = useRef<(HTMLDivElement | null)[]>([]);

  const workers = useMemo(
    () => getCachedWorkers().filter((w) => selectedWorkerIds.includes(w.id)),
    [selectedWorkerIds],
  );

  // Extend the grid upward when a visible zone starts before the opening hour
  // (e.g. a 7h45 zone with an 8h opening) so its title isn't clipped above the top.
  const HOURS_START = useMemo(() => {
    let earliest = openingHour;
    for (const b of blocks) {
      if (!selectedWorkerIds.includes(b.workerId)) continue;
      if (activeZoneFilters.length > 0 && (!b.zoneType || !activeZoneFilters.includes(b.zoneType))) continue;
      if (searchQuery && !b.label.toLowerCase().includes(searchQuery.toLowerCase())) continue;
      if (b.allDay) continue;
      const seg = blockSegmentOnDate(b, selectedDate, openingHour, HOURS_END);
      if (seg && seg.isFirstDay && seg.startHour < earliest) earliest = seg.startHour;
    }
    return Math.floor(earliest);
  }, [blocks, selectedWorkerIds, activeZoneFilters, searchQuery, selectedDate, openingHour, HOURS_END]);

  const hours = useMemo(() => {
    const arr: number[] = [];
    for (let h = HOURS_START; h < HOURS_END; h++) arr.push(h);
    return arr;
  }, [HOURS_START, HOURS_END]);

  const totalGridHeight = (HOURS_END - HOURS_START) * HOUR_BLOCK_HEIGHT;

  const dayBlocks = useMemo(() => {
    return blocks.filter((b) => {
      if (!selectedWorkerIds.includes(b.workerId)) return false;
      if (activeZoneFilters.length > 0 && (!b.zoneType || !activeZoneFilters.includes(b.zoneType))) return false;
      if (searchQuery && !b.label.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return blockSegmentOnDate(b, selectedDate, HOURS_START, HOURS_END) !== null;
    });
  }, [blocks, selectedWorkerIds, selectedDate, searchQuery, activeZoneFilters, HOURS_START, HOURS_END]);

  const blocksByWorker = useMemo(() => {
    const map: Record<string, AvailabilityBlock[]> = {};
    for (const w of workers) map[w.id] = [];
    for (const b of dayBlocks) {
      if (map[b.workerId]) map[b.workerId].push(b);
    }
    return map;
  }, [workers, dayBlocks]);

  // Congé intervals per worker for the selected day — work zones overlapping
  // them are shown neutralised (faded + hatched in the congé colour).
  const congeByWorker = useMemo(() => {
    const m: Record<string, [number, number][]> = {};
    for (const b of dayBlocks) {
      if (b.zoneType !== "conge") continue;
      const seg = blockSegmentOnDate(b, selectedDate, HOURS_START, HOURS_END);
      if (!seg) continue;
      if (!m[b.workerId]) m[b.workerId] = [];
      m[b.workerId].push([seg.startHour, seg.endHour]);
    }
    return m;
  }, [dayBlocks, selectedDate, HOURS_START, HOURS_END]);

  const colIndexFromX = useCallback((clientX: number) => {
    for (let i = 0; i < colRefs.current.length; i++) {
      const el = colRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) return i;
    }
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < colRefs.current.length; i++) {
      const el = colRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const center = (rect.left + rect.right) / 2;
      const dist = Math.abs(clientX - center);
      if (dist < bestDist) { bestDist = dist; best = i; }
    }
    return best;
  }, []);

  const handleBlockPointerDown = useCallback(
    (e: React.PointerEvent, block: AvailabilityBlock, colIndex: number, mode: "move" | "resize") => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      didDrag.current = false;
      blockDragRef.current = { blockId: block.id, mode, startY: e.clientY, startX: e.clientX, origStartHour: block.startHour, origDuration: block.durationHours, origColIndex: colIndex, previewColIndex: colIndex };
      setDragPreview({ blockId: block.id, colIndex, startHour: block.startHour, durationHours: block.durationHours });
    },
    [],
  );

  const handleBlockPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const bd = blockDragRef.current;
      if (!bd) return;
      const deltaY = e.clientY - bd.startY;
      if (Math.abs(deltaY) > 3 || Math.abs(e.clientX - bd.startX) > 3) didDrag.current = true;
      const deltaSteps = Math.round(deltaY / STEP_HEIGHT);
      const deltaHours = deltaSteps * SNAP_STEP;
      const newColIndex = colIndexFromX(e.clientX);
      bd.previewColIndex = newColIndex;

      if (bd.mode === "resize") {
        const newDur = snapToGrid(bd.origDuration + deltaHours, SNAP_STEP);
        const clamped = Math.max(SNAP_STEP, Math.min(newDur, HOURS_END - bd.origStartHour));
        setDragPreview({ blockId: bd.blockId, colIndex: bd.origColIndex, startHour: bd.origStartHour, durationHours: clamped });
      } else {
        const newStart = snapToGrid(bd.origStartHour + deltaHours, SNAP_STEP);
        const clamped = Math.max(HOURS_START, Math.min(newStart, HOURS_END - bd.origDuration));
        setDragPreview({ blockId: bd.blockId, colIndex: newColIndex, startHour: clamped, durationHours: bd.origDuration });
      }
    },
    [colIndexFromX, HOURS_START, HOURS_END],
  );

  const handleBlockPointerUp = useCallback(() => {
    const bd = blockDragRef.current;
    if (!bd) return;
    if (dragPreview && didDrag.current) {
      const targetWorker = workers[bd.previewColIndex];
      updateBlock(bd.blockId, {
        startHour: dragPreview.startHour,
        durationHours: dragPreview.durationHours,
        ...(bd.mode === "move" && targetWorker ? { workerId: targetWorker.id } : {}),
      });
    } else if (!didDrag.current) {
      const block = dayBlocks.find((b) => b.id === bd.blockId);
      if (block) onEditBlock(block);
    }
    blockDragRef.current = null;
    setDragPreview(null);
  }, [dragPreview, updateBlock, workers, dayBlocks, onEditBlock]);

  const handleCellPointerDown = useCallback(
    (e: React.PointerEvent, colIndex: number) => {
      if (blockDragRef.current) return;
      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const relY = e.clientY - rect.top;
      const step = Math.floor(relY / STEP_HEIGHT);
      createDragRef.current = { colIndex, startQuarter: step, currentQuarter: step };
      setDragPreview({ colIndex, startHour: HOURS_START + step * SNAP_STEP, durationHours: SNAP_STEP });
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [HOURS_START],
  );

  const handleCellPointerMove = useCallback(
    (e: React.PointerEvent, colIndex: number) => {
      if (blockDragRef.current) { handleBlockPointerMove(e); return; }
      const cd = createDragRef.current;
      if (!cd || cd.colIndex !== colIndex) return;
      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const relY = Math.max(0, Math.min(e.clientY - rect.top, totalGridHeight));
      const step = Math.floor(relY / STEP_HEIGHT);
      cd.currentQuarter = step;
      const start = Math.min(cd.startQuarter, step);
      const end = Math.max(cd.startQuarter, step) + 1;
      setDragPreview({ colIndex, startHour: HOURS_START + start * SNAP_STEP, durationHours: (end - start) * SNAP_STEP });
    },
    [totalGridHeight, handleBlockPointerMove, HOURS_START],
  );

  const handleCellPointerUp = useCallback(
    (_e: React.PointerEvent, colIndex: number) => {
      if (blockDragRef.current) { handleBlockPointerUp(); return; }
      const cd = createDragRef.current;
      if (!cd || cd.colIndex !== colIndex) return;
      const start = Math.min(cd.startQuarter, cd.currentQuarter);
      const end = Math.max(cd.startQuarter, cd.currentQuarter) + 1;
      const startHour = HOURS_START + start * SNAP_STEP;
      const duration = (end - start) * SNAP_STEP;
      const worker = workers[colIndex];
      if (worker && duration >= SNAP_STEP) {
        setCreateModal({ workerId: worker.id, date: selectedDate, startHour, duration });
      }
      createDragRef.current = null;
      setDragPreview(null);
    },
    [workers, selectedDate, handleBlockPointerUp, HOURS_START],
  );

  return (
    <div className="flex flex-col flex-1" style={{ minWidth: 0 }} onPointerMove={handleBlockPointerMove} onPointerUp={handleBlockPointerUp}>
      {/* Worker name headers */}
      <div className="flex flex-row" style={{ paddingLeft: 60 }}>
        {workers.map((w, i) => (
          <div key={w.id} className="flex-1 text-center text-h4 font-medium py-2" style={{ fontFamily: "Poppins", color: "#525252", borderBottom: "1px solid #E5E5E5", borderRight: i < workers.length - 1 ? "1px solid #E5E5E5" : undefined }}>
            {w.name}
          </div>
        ))}
      </div>

      {/* Grid body */}
      <div className="flex flex-row flex-1 overflow-y-auto" style={{ position: "relative" }}>
        {/* Org closure day indicator (covers all worker columns for this day) */}
        {isDateClosed(selectedDate, closedDates) && <ClosedDayOverlay />}
        {/* Hour labels */}
        <div className="flex flex-col flex-shrink-0" style={{ width: 60 }}>
          {hours.map((h) => (
            <div key={h} className="flex flex-col" style={{ height: HOUR_BLOCK_HEIGHT }}>
              {[0, 15, 30, 45].map((m) => (
                <div key={m} className="flex flex-row items-start" style={{ height: QUARTER_HEIGHT }}>
                  {m === 0 ? (
                    <>
                      <span className="text-h5 font-medium pr-1 text-right" style={{ width: 30, fontFamily: "Poppins", color: "#A1A1A1", marginTop: -6 }}>{h}h</span>
                      <span className="text-h6 font-medium" style={{ fontFamily: "Poppins", color: "#D4D4D4", marginTop: -4 }}>00</span>
                    </>
                  ) : (
                    <>
                      <span style={{ width: 30 }} />
                      <span className="text-h6 font-medium" style={{ fontFamily: "Poppins", color: "#E5E5E5", marginTop: -4 }}>{m}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Worker columns */}
        {workers.map((worker, colIndex) => {
          const wBlocks = blocksByWorker[worker.id] || [];
          const preview = dragPreview && dragPreview.colIndex === colIndex ? dragPreview : null;

          return (
            <div
              key={worker.id}
              ref={(el) => { colRefs.current[colIndex] = el; }}
              className="flex-1 relative"
              style={{ borderRight: colIndex < workers.length - 1 ? "1px solid #E5E5E5" : undefined, cursor: "crosshair" }}
              onPointerDown={(e) => handleCellPointerDown(e, colIndex)}
              onPointerMove={(e) => handleCellPointerMove(e, colIndex)}
              onPointerUp={(e) => handleCellPointerUp(e, colIndex)}
            >
              {hours.map((h) => (
                <div key={h} style={{ height: HOUR_BLOCK_HEIGHT, borderBottom: "1px solid #E5E5E5" }}>
                  {[0, 1, 2, 3].map((q) => (
                    <div key={q} style={{ height: QUARTER_HEIGHT, borderBottom: q < 3 ? "1px solid #F5F5F5" : undefined }} />
                  ))}
                </div>
              ))}

              {/* Create-drag preview */}
              {preview && !preview.blockId && (
                <div className="absolute left-1 right-1 pointer-events-none" style={{ top: (preview.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT, height: preview.durationHours * HOUR_BLOCK_HEIGHT, background: "rgba(99, 102, 241, 0.15)", border: "2px dashed #6366F1", borderRadius: 6, zIndex: 30 }} />
              )}

              {/* Blocks */}
              {wBlocks.map((block) => {
                const colors = getBlockColors(block);
                const seg = blockSegmentOnDate(block, selectedDate, HOURS_START, HOURS_END);
                if (!seg) return null;
                // A work zone overlapping a congé is neutralised → faded + hatched.
                const neutralized = block.zoneType !== "conge" &&
                  (congeByWorker[block.workerId] || []).some(([cs, ce]) => seg.startHour < ce && seg.endHour > cs);
                // Multi-day / all-day blocks render as a fixed segment (edit via modal).
                const multiSpan = !!block.allDay || !seg.isFirstDay || !seg.isLastDay || !!block.endDate;
                const isDragging = dragPreview?.blockId === block.id && !multiSpan;
                const displayStart = isDragging ? dragPreview!.startHour : seg.startHour;
                const displayDur = isDragging ? dragPreview!.durationHours : seg.endHour - seg.startHour;
                const topPx = (displayStart - HOURS_START) * HOUR_BLOCK_HEIGHT;
                const heightPx = displayDur * HOUR_BLOCK_HEIGHT;

                return (
                  <div
                    key={block.id}
                    className="absolute left-1 right-1 flex flex-col overflow-hidden"
                    style={{ top: topPx, height: Math.max(heightPx, QUARTER_HEIGHT), background: colors.bg, border: `1px solid ${colors.border}`, boxShadow: isDragging ? "0px 4px 12px rgba(0,0,0,0.2)" : "0px 2px 4px rgba(0,0,0,0.08)", borderRadius: 6, padding: "4px 6px", zIndex: isDragging ? 35 : 20, cursor: multiSpan ? "pointer" : "grab", userSelect: "none", opacity: isDragging ? 0.85 : neutralized ? 0.5 : 1, transition: isDragging ? undefined : "opacity 0.15s" }}
                    onPointerDown={(e) => {
                      // Always stop propagation so the column underneath doesn't also
                      // start a "create slot" drag (multi-day blocks skip the move drag).
                      e.stopPropagation();
                      if (!multiSpan) handleBlockPointerDown(e, block, colIndex, "move");
                    }}
                    onClick={(e) => { if (multiSpan) { e.stopPropagation(); onEditBlock(block); } }}
                  >
                    {/* Neutralised by a congé — hatch in the congé colour */}
                    {neutralized && (
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          borderRadius: 6,
                          background: `repeating-linear-gradient(45deg, ${ZONE_COLORS.conge.text}33, ${ZONE_COLORS.conge.text}33 5px, transparent 5px, transparent 11px)`,
                        }}
                        title="Ignoré (congé)"
                      />
                    )}
                    <span className="text-h5 font-medium" style={{ color: colors.text, lineHeight: "140%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{block.label || ZONE_COLORS[block.zoneType || "mecanique"].label}</span>
                    {heightPx >= 50 && !block.allDay && <span className="text-h6" style={{ color: colors.text, opacity: 0.7 }}>{formatHour(displayStart)} – {formatHour(displayStart + displayDur)}</span>}
                    {heightPx >= 40 && (block.recurrence === "annual" || block.recurring) && (
                      <div className="absolute bottom-1 right-1" style={{ zIndex: 25 }}>
                        <div className="flex items-center justify-center rounded" style={{ width: 18, height: 18, background: "rgba(255,255,255,0.6)" }}>
                          <Icon1 name="restart" size={12} color={colors.text} />
                        </div>
                      </div>
                    )}
                    {!multiSpan && (
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center items-center" style={{ height: 8, cursor: "ns-resize" }} onPointerDown={(e) => { e.stopPropagation(); handleBlockPointerDown(e, block, colIndex, "resize"); }}>
                        <div style={{ width: 20, height: 2, borderRadius: 1, background: colors.text, opacity: 0.4 }} />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Ghost preview when dragging block TO this column */}
              {dragPreview && dragPreview.blockId && dragPreview.colIndex === colIndex && (() => {
                const origBlock = dayBlocks.find((b) => b.id === dragPreview.blockId);
                if (!origBlock) return null;
                const origColIndex = workers.findIndex((w) => w.id === origBlock.workerId);
                if (origColIndex === colIndex) return null;
                const colors = getBlockColors(origBlock);
                const topPx = (dragPreview.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
                const heightPx = dragPreview.durationHours * HOUR_BLOCK_HEIGHT;
                return (
                  <div key="ghost" className="absolute left-1 right-1 pointer-events-none" style={{ top: topPx, height: Math.max(heightPx, QUARTER_HEIGHT), background: `${colors.bg}99`, border: `2px dashed ${colors.border}`, borderRadius: 6, zIndex: 30 }} />
                );
              })()}
            </div>
          );
        })}
      </div>

      {createModal && (
        <BlockModal mode="create" workerId={createModal.workerId} initialDate={createModal.date} initialStartHour={createModal.startHour} initialDuration={createModal.duration} onClose={() => setCreateModal(null)} />
      )}
    </div>
  );
}
